import HeroSearch from "../components/HeroSearch";
import TrustStrip from "../components/TrustStrip";
import TestimonialStrip from "../components/TestimonialStrip";
import PricingBlock from "../components/PricingBlock";
import TrustStatement from "../components/TrustStatement";
import { Link } from "react-router-dom";
import { ArrowRight, Search, FileText, Lock, ShieldCheck, PiggyBank, Scale, Clock, Globe2, Building2, CheckCircle2, XCircle, Phone, Mail, Award } from "lucide-react";
import { EditableText, useContentText } from "../lib/content";
import { useSeo } from "../lib/useSeo";

const WHY_BENEFITS = [
  { icon: Scale, key1: "why.benefit_1.title", default1: "Up to £10,000 passing-off legal assistance", key2: "why.benefit_1.body", default2: "If a competitor encroaches on your name, we contribute up to £10,000 towards passing-off legal assistance. You are never alone in a dispute." },
  { icon: PiggyBank, key1: "why.benefit_2.title", default1: "Avoid £4,000 to £20,000 re-branding", key2: "why.benefit_2.body", default2: "A forced re-brand costs UK small businesses £4,000 to £20,000, plus 12-18 months of lost SEO and customer recognition." },
  { icon: FileText, key1: "why.benefit_3.title", default1: "Legally-recognised proof of use", key2: "why.benefit_3.body", default2: "Your dated, time-stamped record of trading is the bedrock of any UK passing-off claim. We build it for you, automatically." },
  { icon: Clock, key1: "why.benefit_4.title", default1: "Real UK legal team, on call", key2: "why.benefit_4.body", default2: "Speak to a real person within one working day. No bots, no offshoring, no jargon." },
];

const HOW_STEPS = [
  { n: "01", icon: Search, key_title: "home.how.step1.title", default_title: "Search your business name", key_body: "home.how.step1.body", default_body: "Free check. Companies House similarity and domain availability across 5 major TLDs. 30 seconds." },
  { n: "02", icon: FileText, key_title: "home.how.step2.title", default_title: "Receive your risk report", key_body: "home.how.step2.body", default_body: "Clear risk score, specific overlaps, and an action plan. No legal jargon." },
  { n: "03", icon: Lock, key_title: "home.how.step3.title", default_title: "Get protected from £15 a month", key_body: "home.how.step3.body", default_body: "Subscribe, fill in your details, secure payment via Stripe. Live the same day." },
];

export default function Home() {
  const phone = useContentText("footer.contact.phone", "0800 069 9090");
  const phoneDigits = phone.replace(/\D/g, "");
  const email = useContentText("footer.contact.email", "info@nbrg.co.uk");

  useSeo({
    title: "UK Business Name Protection - Check & Protect Your Name",
    description: "Run a free 30-second name check across Companies House and domains, get a clear risk report, then protect your UK business name from £15/mo. Trusted by 2000+ businesses.",
    appendBrand: false,
  });

  return (
    <main data-testid="home-page">
      {/* HERO - soft light, calm & search-first */}
      <section className="relative overflow-hidden bg-[#f7f7f7] pt-10 md:pt-14 pb-14 md:pb-16" data-testid="home-hero">
        <div className="absolute inset-0 bnp-radial-glow pointer-events-none" />
        <div className="absolute inset-0 bnp-grid-bg opacity-[0.28] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 relative">
          <div className="text-center max-w-5xl mx-auto">
            <span className="bnp-chip bnp-chip-teal" data-testid="hero-eyebrow">
              <EditableText keyName="home.hero.eyebrow" default="The UK's only business name protection service" />
            </span>
            <h1 className="font-display text-[clamp(2rem,4vw,3.4rem)] text-[#04273d] mt-4 font-semibold tracking-tight leading-[1.12]">
              <EditableText keyName="home.hero.headline_line1" default="Protect your UK business name with confidence." />
            </h1>
            <p className="font-display text-lg sm:text-[clamp(1.35rem,2.4vw,2rem)] text-[#447694] mt-2.5 font-medium tracking-tight leading-snug max-w-3xl mx-auto">
              <EditableText keyName="home.hero.headline_line2" default="Simple, affordable protection built for real businesses." />
            </p>
            <p className="text-[#447694] text-base md:text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
              <EditableText keyName="home.hero.subheadline" default="Check your name in seconds. Then secure your dated proof of use, with up to £10,000 of passing-off legal assistance included, from just £15 a month, including VAT." />
            </p>
          </div>

          {/* Search bar centered */}
          <div className="mt-8 md:mt-10 max-w-3xl mx-auto">
            <HeroSearch />
            <p className="text-sm text-[#447694] mt-3 text-center">
              <EditableText keyName="home.hero.helper" default="Free check. No card needed. Instant risk report with clear, prioritised recommendations." />
            </p>
            {/* Quick contact strip - sits right under the Check button so visitors who'd rather call us can. */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5" data-testid="home-hero-contact-row">
              <span className="text-xs font-mono-brand tracking-widest uppercase text-[#447694] mr-1">Prefer to talk?</span>
              <a
                href={`tel:${phoneDigits}`}
                className="inline-flex items-center gap-2 bg-white border border-[#9ac7f7]/45 hover:border-[#0d9b80] hover:text-[#0d9b80] text-[#04273d] font-display font-semibold text-sm px-4 py-2.5 rounded-[4px] transition-colors"
                data-testid="home-hero-call-button"
              >
                <Phone size={14} /> {phone}
              </a>
              <a
                href={`mailto:${email}`}
                className="inline-flex items-center gap-2 bg-white border border-[#9ac7f7]/45 hover:border-[#0d9b80] hover:text-[#0d9b80] text-[#04273d] font-display font-semibold text-sm px-4 py-2.5 rounded-[4px] transition-colors"
                data-testid="home-hero-email-button"
              >
                <Mail size={14} /> {email}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* HERITAGE + PREVIEW - light content section */}
      <section className="bg-[#f7f7f7] pt-4 md:pt-6 pb-16 border-b border-[#9ac7f7]/25" data-testid="home-heritage-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 relative">
          {/* Heritage + award badges - the NBR Group 'human/established' proof */}
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-12 max-w-5xl mx-auto">
            <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-white border border-[#9ac7f7]/45 rounded-[5px] px-2.5 sm:px-3 py-1.5" data-testid="home-award-badge">
              <Award size={13} className="text-[#0d9b80] shrink-0" />
              <span className="font-mono-brand text-[9px] sm:text-[10px] tracking-wide uppercase text-[#04273d] font-semibold leading-tight whitespace-nowrap">Product of the Year 2024</span>
            </div>
            <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-white border border-[#9ac7f7]/45 rounded-[5px] px-2.5 sm:px-3 py-1.5" data-testid="home-heritage-badge">
              <ShieldCheck size={13} className="text-[#0d9b80] shrink-0" />
              <span className="font-mono-brand text-[9px] sm:text-[10px] tracking-wide uppercase text-[#04273d] font-semibold leading-tight whitespace-nowrap">NBR Group · Since 1984</span>
            </div>
          </div>

          <div className="max-w-3xl mx-auto">
            <p className="text-xs text-[#447694] tracking-widest uppercase font-semibold mb-3 text-center">
              <EditableText keyName="home.sample.eyebrow" default="A preview of what you'll get" />
            </p>
            <SampleReportPreview />
            <p className="text-xs text-[#447694] mt-3 italic text-center">
              <EditableText keyName="home.sample.caption" default="Run a free check above to get your own personalised report you can download as a PDF." />
            </p>
          </div>

          <div className="mt-10">
            <TrustStrip />
          </div>

          {/* £10,000 highlight banner */}
          <div className="mt-10 max-w-4xl mx-auto bnp-cta-band rounded-3xl p-6 md:p-7 flex flex-col sm:flex-row items-center justify-between gap-5 relative" data-testid="home-tenk-banner">
            <div className="flex items-center gap-4 relative">
              <div className="h-14 w-14 rounded-2xl bg-[#04273d] flex items-center justify-center shrink-0">
                <Scale size={26} className="text-[#71edc9]" />
              </div>
              <div>
                <p className="font-display text-2xl md:text-3xl font-bold text-[#04273d]">
                  <EditableText keyName="home.tenk.title" default="Up to £10,000 passing-off legal assistance" />
                </p>
                <p className="text-sm text-[#04273d]/80 mt-1 font-medium">
                  <EditableText keyName="home.tenk.body" default="Real legal backing if a competitor ever copies your name. Included with every subscription." />
                </p>
              </div>
            </div>
            <Link to="/pricing" className="relative bg-[#04273d] text-white hover:bg-[#0a3b59] transition-colors px-7 py-4 rounded-[4px] font-display font-bold text-base whitespace-nowrap flex items-center gap-2 shrink-0" data-testid="home-tenk-banner-cta">
              <EditableText keyName="home.tenk.cta" default="Get protected now" /> <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY PROTECT YOUR NAME - navy anchor section for contrast */}
      <section className="py-16 md:py-24 lg:py-28 xl:py-32 bnp-section-navy" data-testid="why-protect-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="bnp-chip bnp-chip-mint">
              <EditableText keyName="why.eyebrow" default="Why protect your business name" />
            </span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-white mt-3 font-semibold tracking-tight">
              <EditableText keyName="why.title" default="Four reasons UK businesses choose us." />
            </h2>
            <p className="text-[#9ac7f7] mt-3">
              <EditableText keyName="why.subtitle" default="Real outcomes for real businesses. No legal jargon, no marketing fluff." />
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {WHY_BENEFITS.map((b, i) => (
              <div key={i} className="bnp-card-dark p-6 hover:-translate-y-1" data-testid={`why-benefit-${i}`}>
                <div className="h-10 w-10 rounded-xl bg-[#71edc9]/15 flex items-center justify-center">
                  <b.icon size={18} className="text-[#71edc9]" />
                </div>
                <h3 className="font-display text-base text-white mt-4 font-semibold leading-snug">
                  <EditableText keyName={b.key1} default={b.default1} />
                </h3>
                <p className="text-sm text-[#9ac7f7] mt-2 leading-relaxed">
                  <EditableText keyName={b.key2} default={b.default2} />
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT VIDEO - sits directly under the benefits so visitors get a 90-second human walkthrough */}
      <section className="py-12 md:py-16 lg:py-20 xl:py-24 bg-white" data-testid="home-video-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center max-w-2xl mx-auto mb-7">
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] font-semibold tracking-tight">
              <EditableText keyName="home.video.title" default="Watch how Business Name Protection works." />
            </h2>
          </div>
          <div className="relative w-full rounded-3xl overflow-hidden bnp-soft-shadow border border-[#9ac7f7]/25" style={{ paddingBottom: "56.25%" }}>
            <iframe
              src="https://www.youtube.com/embed/8MVb7NIrQwQ?rel=0"
              title="Business Name Protection by NBR Group"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="home-video-iframe"
            />
          </div>
        </div>
      </section>

      {/* WHAT IS BNP - soft mint wash */}
      <section className="py-16 md:py-24 lg:py-28 xl:py-32 bnp-section-mint" data-testid="what-is-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center max-w-3xl mx-auto">
            <span className="bnp-chip bnp-chip-light">
              <EditableText keyName="home.what_is.eyebrow" default="What is Business Name Protection?" />
            </span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-semibold tracking-tight">
              <EditableText keyName="home.what_is.title" default="An affordable layer of protection. Not a trade mark. Not a guess. Real, structured cover." />
            </h2>
            <p className="text-[#447694] text-base mt-5 leading-relaxed">
              <EditableText keyName="home.what_is.body" default="Business Name Protection is a UK service that records your business name use, builds your legal proof of trading, and gives you up to **£10,000** of passing-off legal assistance if your name is ever copied. It costs from £15 per month including VAT. No setup fees. No legal forms to fill in. No surprise bills." />
            </p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto mt-8">
            {[
              { k: "home.what_is.bullet_1", d: "Dated proof of use, recorded from day one." },
              { k: "home.what_is.bullet_2", d: "Up to £10,000 passing-off legal assistance." },
              { k: "home.what_is.bullet_3", d: "Real UK team, on call when you need them." },
              { k: "home.what_is.bullet_4", d: "From £15 per month. Cancel anytime." },
            ].map((b) => (
              <li key={b.k} className="flex items-start gap-2.5 bg-white border border-[#9ac7f7]/40 rounded-2xl px-4 py-3 shadow-sm">
                <CheckCircle2 size={18} className="text-[#71edc9] shrink-0 mt-0.5" />
                <span className="text-sm text-[#04273d]">
                  <EditableText keyName={b.k} default={b.d} />
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 md:py-24 lg:py-28 xl:py-32 bg-white border-y border-[#9ac7f7]/25" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="bnp-chip bnp-chip-light">
              <EditableText keyName="home.how.eyebrow" default="How it works" />
            </span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-semibold tracking-tight">
              <EditableText keyName="home.how.title" default="Three simple steps to a protected business name." />
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {HOW_STEPS.map((s, i) => (
              <div key={s.n} className="bnp-card p-6 md:p-7" data-testid={`how-step-${i}`}>
                <span className="font-display text-xs text-[#447694] tracking-widest uppercase font-semibold">{s.n}</span>
                <div className="h-11 w-11 rounded-xl bg-[#71edc9]/30 flex items-center justify-center mt-3">
                  <s.icon size={20} className="text-[#04273d]" />
                </div>
                <h3 className="font-display text-xl text-[#04273d] mt-4 font-semibold">
                  <EditableText keyName={s.key_title} default={s.default_title} />
                </h3>
                <p className="text-sm text-[#447694] mt-2 leading-relaxed">
                  <EditableText keyName={s.key_body} default={s.default_body} />
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Link to="/how-it-works" className="btn-secondary" data-testid="home-how-cta">
              <EditableText keyName="home.how.cta" default="See the full process" /> <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS - soft blue wash */}
      <div className="bnp-section-blue" data-testid="home-testimonials-wrap">
        <TestimonialStrip />
      </div>

      {/* PRICING TEASER */}
      <section className="py-16 md:py-24 lg:py-28 xl:py-32 bg-white border-y border-[#9ac7f7]/25" data-testid="home-pricing-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="bnp-chip bnp-chip-light">
              <EditableText keyName="home.pricing.eyebrow" default="Pricing" />
            </span>
            <h2 className="font-display text-[clamp(1.75rem,3.2vw,3rem)] text-[#04273d] mt-3 font-semibold tracking-tight">
              <EditableText keyName="home.pricing.title" default="Fixed, transparent pricing. No legal fees." />
            </h2>
            <p className="text-[#447694] mt-3">
              <EditableText keyName="home.pricing.body" default="From £15 a month including VAT. Up to £10,000 passing-off legal assistance included." />
            </p>
          </div>
          <PricingBlock />
        </div>
      </section>

      {/* FINAL CTA, bright teal band, impossible to skim past */}
      <section className="py-20" data-testid="home-final-cta">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
          <div className="bnp-cta-band rounded-[28px] p-10 md:p-14 text-center relative">
            <ShieldCheck size={40} className="text-[#04273d] mx-auto relative" />
            <h2 className="font-display text-[clamp(1.75rem,3.5vw,3rem)] text-[#04273d] mt-4 font-bold tracking-tight relative">
              <EditableText keyName="home.final_cta.title" default="Your name is your business. Protect it today." />
            </h2>
            <p className="text-[#04273d]/80 text-base md:text-lg mt-4 max-w-2xl mx-auto font-medium relative">
              <EditableText keyName="home.final_cta.body" default="Run a free 30-second check. Get a clear, actionable risk report with specific recommendations. Then decide if protection is right for you." />
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 relative">
              <Link to="/check" className="bg-[#04273d] text-white hover:bg-[#0a3b59] transition-colors px-8 py-4 rounded-[4px] font-display font-bold text-base inline-flex items-center gap-2" data-testid="home-final-cta-check">
                <EditableText keyName="home.final_cta.button_check" default="Check my name free" /> <ArrowRight size={16} />
              </Link>
              <Link to="/pricing" className="bg-white text-[#04273d] hover:bg-[#f7f7f7] transition-colors px-8 py-4 rounded-[4px] font-display font-bold text-base inline-flex items-center gap-2 border-2 border-[#04273d]" data-testid="home-final-cta-protect">
                <EditableText keyName="home.final_cta.button_protect" default="Get protected from £15/mo" />
              </Link>
            </div>
            <TrustStatement className="mt-7 relative" />
          </div>
        </div>
      </section>
    </main>
  );
}

function SampleReportPreview() {
  return (
    <div className="relative" data-testid="home-sample-report-preview">
      <div className="absolute -inset-3 bg-[#71edc9]/15 blur-3xl rounded-3xl pointer-events-none" />
      <div className="bnp-card p-6 md:p-7 relative">
        {/* Header strip */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="bnp-chip bnp-chip-navy text-[10px]">Sample Report</span>
            <p className="font-display font-semibold text-[#04273d] text-sm">Crown Hair Studio</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="bnp-chip bnp-chip-warn text-[10px]">High risk</span>
            <div className="flex items-baseline gap-1">
              <span className="font-display text-2xl font-bold text-[#04273d]">68</span>
              <span className="font-display text-[10px] text-[#447694]">/100</span>
            </div>
          </div>
        </div>
        <div className="h-1.5 w-full rounded-full bg-[#9ac7f7]/30 mt-3 overflow-hidden">
          <div className="h-full bg-amber-500" style={{ width: "68%" }} />
        </div>

        {/* Two-column grid (Companies House + Domains). Social handles was removed
            until we wire up real-time aggregator checks. */}
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <div className="bg-[#f7f7f7]/70 border border-[#9ac7f7]/25 rounded-xl p-3">
            <div className="flex items-center gap-1.5 text-[10px] text-[#447694] uppercase tracking-widest font-semibold mb-2">
              <Building2 size={12} /> Companies House
            </div>
            <ul className="text-xs text-[#04273d] space-y-1">
              <li className="flex justify-between gap-2"><span className="truncate">Crown Hair &amp; Beauty Ltd</span><span className="text-red-600 text-[10px] shrink-0">94%</span></li>
              <li className="flex justify-between gap-2"><span className="truncate">Crown Hair Studio Ltd</span><span className="text-red-600 text-[10px] shrink-0">88%</span></li>
              <li className="flex justify-between gap-2"><span className="truncate">The Crown Salon Ltd</span><span className="text-amber-600 text-[10px] shrink-0">71%</span></li>
            </ul>
          </div>
          <div className="bg-[#f7f7f7]/70 border border-[#9ac7f7]/25 rounded-xl p-3">
            <div className="flex items-center gap-1.5 text-[10px] text-[#447694] uppercase tracking-widest font-semibold mb-2">
              <Globe2 size={12} /> Domains
            </div>
            <ul className="text-xs text-[#04273d] space-y-1">
              <li className="flex justify-between items-center gap-2"><span className="truncate">crownhairstudio.co.uk</span><span className="text-red-600 text-[10px] flex items-center gap-0.5 shrink-0"><XCircle size={10} />Taken</span></li>
              <li className="flex justify-between items-center gap-2"><span className="truncate">crownhairstudio.com</span><span className="text-red-600 text-[10px] flex items-center gap-0.5 shrink-0"><XCircle size={10} />Taken</span></li>
              <li className="flex justify-between items-center gap-2"><span className="truncate">crownhairstudio.uk</span><span className="text-emerald-600 text-[10px] flex items-center gap-0.5 shrink-0"><CheckCircle2 size={10} />Free</span></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
